import React, { useEffect, useState } from "react";
import instance from "../utils/axiosInstance";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  Pagination,
  Typography,
  Container,
  TextField,
  CircularProgress,
  MenuItem,
  FormControlLabel,
  Checkbox,

} from "@mui/material";
import { getIpfsUrl, getMetaFromTokenURI } from "../utils/meta_in_token";
import NFTCard from "../components/NFTCard";

const NFTList = () => {
  const [nfts, setNfts] = useState([]);
  const navigate = useNavigate();
  const [totalPages, setTotalPages] = useState(1);
  const { page: pageParam } = useParams();
  const [page, setPage] = useState(parseInt(pageParam) || 1);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const isSale = searchParams.get("isSale") === "true";
  const [searchField, setSearchField] = useState("title"); // 검색대상: title/creator
  const [onlyOnSale, setOnlyOnSale] = useState(false); // 판매중만
  const [onlyLazyMint, setOnlyLazyMint] = useState(false); // LazyMint만
  const [sortBy, setSortBy] = useState("views"); // 정렬기준: views/liked


  useEffect(() => {
    const initialKeyword = searchParams.get("keyword") || "";
    const isSaleParam = searchParams.get("isSale") === "true";
    const fieldParam = searchParams.get("field") || "title";
  
    setKeyword(initialKeyword);
    setSearchField(fieldParam); // ⭐ 이걸 안넣으면 searchField는 여전히 'title'
  
    fetchNFTs(page, initialKeyword, isSaleParam, fieldParam); // ⭐ 여기서도 직접 넘겨야 안전
  }, [pageParam, searchParams]);
  

  const fetchNFTs = async (
    pageNum = 1,
    keywordQuery = "",
    isSaleFlag = false,
    field = "title"
  ) => {
    setLoading(true);
    setNfts([]);
    try {
      const res = await instance.get(
        `/list?sort=${sortBy}&order=desc&page=${pageNum}&limit=12&keyword=${keywordQuery}&isSale=${isSaleFlag}&field=${field}`
      );
  
      const rawNFTs = res.data.nfts;
      const processedNFTs = await Promise.all(
        rawNFTs.map(async (nft) => {
          const metadata = await getMetaFromTokenURI(nft.tokenURI);
          return {
            ...nft,
            name: metadata?.name || nft.name,
            description: metadata?.description || nft.description,
            displayImage: getIpfsUrl(metadata?.image),
          };
        })
      );
      setNfts(processedNFTs);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      console.error("NFT 목록 불러오기 실패:", err);
    } finally {
      setLoading(false);
    }
  };
  
  

  const handlePageChange = (e, value) => {
    setPage(value);
    const newParams = {};
    if (keyword) newParams.keyword = keyword;
    if (isSale) newParams.isSale = "true";
    navigate(`/nftlist/${value}?${new URLSearchParams(newParams).toString()}`);
  };

  const handleSearch = () => {
    const params = {
      keyword,
      field: searchField,
      sort: sortBy,
      order: "desc",
      isSale: onlyOnSale ? "true" : undefined,
      isLazy: onlyLazyMint ? "true" : undefined
    };
  
    // params에서 undefined 제거
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== undefined)
    );
  
    setSearchParams(cleanParams);
    navigate(`/nftlist/1?${new URLSearchParams(cleanParams).toString()}`);
  };
  

  return (
    <Container sx={{ mt: 6, mb: 10 }}>
      <Typography variant="h4" fontWeight="bold" mb={4} color="text.primary">
        NFT 마켓
      </Typography>
      <Box sx={{ mb: 4, display: "flex", gap: 2, flexWrap: "wrap" }}>
      <TextField
        label="검색어"
        variant="outlined"
        size="small"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
      />
      <TextField
        select
        label="검색 대상"
        size="small"
        value={searchField}
        onChange={(e) => setSearchField(e.target.value)}
      >
        <MenuItem value="title">제목/설명</MenuItem>
        <MenuItem value="creator">제작자</MenuItem>
        <MenuItem value="owner">소유자</MenuItem>
        
      </TextField>
      <TextField
        select
        label="정렬 기준"
        size="small"
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value)}
      >
        <MenuItem value="views">조회수 순</MenuItem>
        <MenuItem value="liked">좋아요 순</MenuItem>
        <MenuItem value="createdAt">최신순</MenuItem>
      </TextField>
      <FormControlLabel
        control={<Checkbox checked={onlyOnSale} onChange={(e) => setOnlyOnSale(e.target.checked)} />}
        label="판매중만"
      />
      <FormControlLabel
        control={<Checkbox checked={onlyLazyMint} onChange={(e) => setOnlyLazyMint(e.target.checked)} />}
        label="Lazy Mint만"
      />
      <Button variant="contained" onClick={handleSearch}>
        검색
      </Button>
    </Box>

      

      {/* NFT 리스트 */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={10}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {nfts.length === 0 ? (
            <Typography textAlign="center" color="text.secondary">
              표시할 NFT가 없습니다.
            </Typography>
          ) : (
            <Grid container spacing={3}>
              {nfts.map((nft, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={nft._id}>
                  <NFTCard
                    item={nft}
                    index={index}
                    onClick={() => navigate(`/nft/${nft._id}`)}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* 페이지네이션 */}
      <Pagination
        count={totalPages}
        page={page}
        onChange={handlePageChange}
        sx={{ mt: 6, display: "flex", justifyContent: "center" }}
      />
    </Container>
  );
};

export default NFTList;
